import {itemPriceCents, resolutionLabel} from './format';
import type {CartItemV1} from './types';

export const CART_GROUP_BY_VALUES = ['portfolio', 'property', 'source'] as const;
export type CartGroupBy = (typeof CART_GROUP_BY_VALUES)[number];

export const isCartGroupBy = (v: unknown): v is CartGroupBy =>
  typeof v === 'string' && (CART_GROUP_BY_VALUES as readonly string[]).includes(v);

/**
 * The grouping a `?groupBy=` param asks for. An unusable value derives to portfolio instead of
 * being rewritten, so the control never holds a value its options don't offer.
 */
export const resolveCartGroupBy = (
  param: unknown,
  propertyGroupingIsAvailable: boolean
): CartGroupBy =>
  isCartGroupBy(param) && (param !== 'property' || propertyGroupingIsAvailable)
    ? param
    : 'portfolio';

/** The group-by control's options. Property is offered only when it would group something. */
export const cartGroupByOptions = (
  propertyGroupingIsAvailable: boolean
): {value: CartGroupBy; label: string}[] => [
  {value: 'portfolio', label: 'Portfolio'},
  {value: 'source', label: 'Source'},
  ...(propertyGroupingIsAvailable ? [{value: 'property' as const, label: 'Property'}] : []),
];

/**
 * A section of the cart or of a placed order as it appears on screen. Display grouping never bills:
 * the endpoint refuses a checkout spanning contracts, so submit fans out over
 * `groupCartItemsByContract` regardless of which view is on screen.
 */
export interface DisplayGroup<T> {
  key: string;
  label: string;
  /** Secondary line under the heading: the property view names the project the property belongs to. */
  sublabel: string | null;
  items: T[];
  subtotalCents: number;
  acres: number;
}

export type CartDisplayGroup = DisplayGroup<CartItemV1>;

/** How to classify and measure one entry. The cart measures its quote; an order, what was billed. */
export interface GroupMeasure<T> {
  classify: (entry: T) => {key: string; label: string; sublabel: string | null};
  /** Sensing times are ISO-8601 in a fixed zone, so comparing the raw strings orders them. */
  sortKey: (entry: T) => string;
  cents: (entry: T) => number;
  acres: (entry: T) => number;
}

const CART_MEASURE = {
  sortKey: (item: CartItemV1) => item.sensingTime,
  cents: itemPriceCents,
  acres: (item: CartItemV1) => item.billableAcres,
} satisfies Omit<GroupMeasure<CartItemV1>, 'classify'>;

/**
 * Buckets entries by a caller-derived key and totals each bucket, groups alphabetical and entries
 * newest first. The shared shape behind the portfolio, property and source views, which differ only
 * in how they classify and measure an entry.
 */
export function bucketDisplayGroups<T>(entries: T[], measure: GroupMeasure<T>): DisplayGroup<T>[] {
  const order: string[] = [];
  const draft = new Map<string, {label: string; sublabel: string | null; items: T[]}>();

  for (const entry of entries) {
    const {key, label, sublabel} = measure.classify(entry);
    let group = draft.get(key);
    if (!group) {
      group = {label, sublabel, items: []};
      draft.set(key, group);
      order.push(key);
    }
    group.items.push(entry);
  }

  return order
    .map((key) => {
      const group = draft.get(key)!;
      group.items.sort((a, b) => measure.sortKey(b).localeCompare(measure.sortKey(a)));
      return {
        key,
        label: group.label,
        sublabel: group.sublabel,
        items: group.items,
        subtotalCents: group.items.reduce((total, entry) => total + measure.cents(entry), 0),
        acres: group.items.reduce((total, entry) => total + measure.acres(entry), 0),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

export const propertyGroupKey = (item: CartItemV1) =>
  `property-${item.featureCollectionId}-${item.featureId}`;

/** True when ≥2 items share a feature, i.e. property grouping would group something. */
export function propertyGroupingAvailable(items: CartItemV1[]): boolean {
  const seen = new Set<string>();
  for (const item of items) {
    const key = propertyGroupKey(item);
    if (seen.has(key)) {
      return true;
    }
    seen.add(key);
  }
  return false;
}

/** The display-only views never feed submit — only `groupCartItemsByContract` does. */
export function groupCartItems(items: CartItemV1[], groupBy: CartGroupBy): CartDisplayGroup[] {
  switch (groupBy) {
    case 'portfolio':
      return bucketDisplayGroups(items, {
        ...CART_MEASURE,
        classify: (item) => ({
          key: `portfolio-${item.projectId}`,
          label: item.snapshot.projectName,
          sublabel: null,
        }),
      });
    case 'property':
      return bucketDisplayGroups(items, {
        ...CART_MEASURE,
        classify: (item) => ({
          key: propertyGroupKey(item),
          label: item.snapshot.featureName,
          sublabel: item.snapshot.projectName,
        }),
      });
    case 'source':
      return bucketDisplayGroups(items, {
        ...CART_MEASURE,
        classify: (item) => ({
          key: `source-${item.sourceId}`,
          // A source group's rows name their project rather than the source, so the card headline
          // carries the source's resolution.
          label: [item.snapshot.sourceDisplayName, resolutionLabel(item.sourceId)]
            .filter(Boolean)
            .join(' · '),
          sublabel: null,
        }),
      });
  }
}
